import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { logger } from '../utils/logger';


// Hook for debouncing values
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Hook for throttling function calls
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const lastRun = useRef(Date.now());

  return useCallback(
    ((...args) => {
      if (Date.now() - lastRun.current >= delay) {
        callback(...args);
        lastRun.current = Date.now();
      }
    }) as T,
    [callback, delay]
  );
}

// Hook for memoizing expensive calculations
export function useExpensiveCalculation<T, R>(
  calculation: (value: T) => R,
  value: T,
  deps: React.DependencyList = []
): R {
  return useMemo(() => calculation(value), [value, ...deps]);
}

// Hook for virtual scrolling
export function useVirtualScroll<T>({
  items,
  itemHeight: itemHeightProp,
  containerHeight,
  overscan = 5,
}: {
  items: T[];
  itemHeight: number | ((index: number) => number);
  containerHeight: number;
  overscan?: number;
}) {
  const [scrollTop, setScrollTop] = useState(0);

  const itemHeight = typeof itemHeightProp === 'function' 
    ? itemHeightProp 
    : () => itemHeightProp;

  const totalHeight = useMemo(() => {
    return items.reduce((sum, _, index) => sum + itemHeight(index), 0);
  }, [items, itemHeight]);

  const visibleRange = useMemo(() => {
    let startIndex = 0;
    let endIndex = items.length - 1;

    let currentTop = 0;
    for (let i = 0; i < items.length; i++) {
      const height = itemHeight(i);
      if (currentTop + height > scrollTop) {
        startIndex = Math.max(0, i - overscan);
        break;
      }
      currentTop += height;
    }

    currentTop = 0;
    for (let i = 0; i < items.length; i++) {
      const height = itemHeight(i);
      currentTop += height;
      if (currentTop > scrollTop + containerHeight) {
        endIndex = Math.min(items.length - 1, i + overscan);
        break;
      }
    }

    return { startIndex, endIndex };
  }, [scrollTop, containerHeight, items.length, itemHeight, overscan]);

  const visibleItems = useMemo(() => {
    const { startIndex, endIndex } = visibleRange;
    return items.slice(startIndex, endIndex + 1).map((item, index) => ({
      item,
      index: startIndex + index,
    }));
  }, [items, visibleRange]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  return {
    visibleItems,
    totalHeight,
    handleScroll,
  };
}

// Hook for performance monitoring
export function usePerformanceMonitor(componentName: string) {
  const renderCount = useRef(0);
  const startTime = useRef(performance.now());

  useEffect(() => {
    renderCount.current += 1;
  });

  useEffect(() => {
    const endTime = performance.now();
    const renderTime = endTime - startTime.current;
    
    if (process.env.NODE_ENV === 'development') {
      logger.log(`${componentName} render #${renderCount.current}: ${renderTime.toFixed(2)}ms`);
    }
    
    startTime.current = performance.now();
  });

  return {
    renderCount: renderCount.current,
  };
}

// Hook for intersection observer (lazy loading)
export function useIntersectionObserver(
  options: IntersectionObserverInit = {}
) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      options
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [options, hasIntersected]);

  return { ref, isIntersecting, hasIntersected };
}

// Hook for measuring component performance
export function useComponentPerformance(componentName: string) {
  const metrics = useRef({
    renderCount: 0,
    totalRenderTime: 0,
    averageRenderTime: 0,
    lastRenderTime: 0,
  });

  const startTime = useRef(performance.now());

  useEffect(() => {
    const endTime = performance.now();
    const renderTime = endTime - startTime.current;
    
    metrics.current.renderCount += 1;
    metrics.current.totalRenderTime += renderTime;
    metrics.current.averageRenderTime = metrics.current.totalRenderTime / metrics.current.renderCount;
    metrics.current.lastRenderTime = renderTime;
    
    startTime.current = performance.now();
  });

  return metrics.current;
}
